import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import RouteIcon from '@mui/icons-material/Route';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { driverLogService, truckService } from '../api/api';
import { useAuth } from '../auth/AuthContext';
import { formatPortalDate } from '../utils/dateFormat';

function vehicleLabel(truck) {
  if (!truck) return 'Vehicle not found';
  const name = truck.name || [truck.year, truck.make, truck.model].filter(Boolean).join(' ') || `Vehicle #${truck.id}`;
  return truck.license_plate ? `${name} • ${truck.license_plate}` : name;
}

function formatDateTime(value, dateOnly = false) {
  if (!value) return '—';
  const date = new Date(dateOnly ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return String(value);
  return formatPortalDate(date, { includeTime: !dateOnly });
}

function inspectionLabel(type) {
  if (type === 'pre_trip') return 'Pre-Trip Check-In';
  if (type === 'post_trip') return 'Post-Trip Check-In';
  if (type === 'defect_report') return 'Defect Report';
  return 'Inspection';
}

function activityIcon(type) {
  if (type === 'fuel') return <LocalGasStationIcon sx={{ color: '#1565c0' }} />;
  if (type === 'trip') return <RouteIcon sx={{ color: '#1565c0' }} />;
  if (type === 'pre_trip') return <FactCheckIcon sx={{ color: '#2e7d32' }} />;
  if (type === 'post_trip') return <AssignmentTurnedInIcon sx={{ color: '#1565c0' }} />;
  return <WarningAmberIcon sx={{ color: '#ed6c02' }} />;
}

function statusChip(activity) {
  if (activity.kind === 'trip') {
    if (activity.status === 'active') return <Chip size="small" label="Active" color="info" />;
    return activity.status === 'draft'
      ? <Chip size="small" label="Draft" color="warning" />
      : <Chip size="small" label="Submitted" color="success" />;
  }
  if (activity.kind === 'inspection') {
    const status = String(activity.status || '').toLowerCase();
    if (status === 'pass') return <Chip size="small" label="Pass" color="success" />;
    if (status === 'fail') return <Chip size="small" label="Fail" color="error" />;
    if (status === 'needs_attention') return <Chip size="small" label="Needs Attention" color="warning" />;
  }
  return null;
}

export default function DriverActivities() {
  const { hasFullAccess } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [type, setType] = useState('all');
  const [q, setQ] = useState('');

  const load = async ({ quiet = false } = {}) => {
    try {
      if (!quiet) setLoading(true);
      setError('');
      const [inspectionResponse, fuelResponse, tripResponse] = await Promise.all([
        truckService.getAllInspections({ page: 0, pageSize: 50 }),
        driverLogService.getFuelLogs({ all: hasFullAccess }),
        driverLogService.getTripLogs({ all: hasFullAccess }),
      ]);

      const inspections = (inspectionResponse.data?.items || []).map((wrapped) => {
        const inspection = wrapped.inspection || wrapped;
        const otherDriver = inspection?.details?.other_driver_name || inspection?.details?.co_driver_name || '';
        return {
          id: `inspection-${inspection.id}`,
          kind: 'inspection',
          type: inspection.inspection_type,
          title: inspectionLabel(inspection.inspection_type),
          driver: inspection.inspected_by || 'Driver',
          otherDriver,
          vehicle: vehicleLabel(wrapped.truck),
          occurredAt: inspection.occurred_at,
          sortTime: new Date(inspection.occurred_at || 0).getTime(),
          odometer: inspection.odometer,
          status: inspection.status,
          notes: inspection.issues,
        };
      });

      const fuelLogs = (Array.isArray(fuelResponse.data) ? fuelResponse.data : []).map((record) => ({
        id: `fuel-${record.log.id}`,
        kind: 'fuel',
        type: 'fuel',
        title: 'Fuel Record',
        driver: record.log.driver_name || 'Driver',
        vehicle: vehicleLabel(record.truck),
        occurredAt: record.log.fueled_at,
        sortTime: new Date(record.log.fueled_at || 0).getTime(),
        details: `${record.log.quantity_litres} L${record.log.total_cost ? ` • $${record.log.total_cost}` : ''}`,
        odometer: record.log.odometer,
        notes: [record.log.fuel_type, record.log.station, record.log.notes].filter(Boolean).join(' • '),
      }));

      const tripLogs = (Array.isArray(tripResponse.data) ? tripResponse.data : []).map((record) => ({
        id: `trip-${record.log.id}`,
        kind: 'trip',
        type: 'trip',
        title: record.log.title || 'Trip Log',
        driver: record.log.driver_name || 'Driver',
        vehicle: vehicleLabel(record.truck),
        occurredAt: record.log.started_at || record.log.trip_date,
        sortTime: new Date(record.log.started_at || `${record.log.trip_date}T00:00:00`).getTime(),
        details: record.log.status === 'active'
          ? `Began at ${Number(record.log.beginning_odometer).toLocaleString()} km`
          : `${Number(record.log.total_trip || 0).toLocaleString()} km`,
        odometer: record.log.status === 'active' ? null : record.log.ending_odometer,
        status: record.log.status,
        notes: record.log.notes,
        dateOnly: !record.log.started_at,
      }));

      setActivities([...inspections, ...fuelLogs, ...tripLogs].sort((a, b) => b.sortTime - a.sortTime));
    } catch (loadError) {
      setError(loadError?.response?.data?.error || loadError?.message || 'Failed to load driver activity.');
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const refreshQuietly = () => {
      if (document.visibilityState === 'visible') load({ quiet: true });
    };
    const interval = window.setInterval(refreshQuietly, 10_000);
    window.addEventListener('focus', refreshQuietly);
    window.addEventListener('dataUpdated', refreshQuietly);
    document.addEventListener('visibilitychange', refreshQuietly);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshQuietly);
      window.removeEventListener('dataUpdated', refreshQuietly);
      document.removeEventListener('visibilitychange', refreshQuietly);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredActivities = useMemo(() => {
    const query = q.trim().toLowerCase();
    return activities.filter((activity) => {
      const matchesType = type === 'all' || activity.type === type || activity.kind === type;
      const matchesQuery = !query || [activity.title, activity.driver, activity.otherDriver, activity.vehicle, activity.details, activity.notes]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
      return matchesType && matchesQuery;
    });
  }, [activities, q, type]);

  return (
    <Box>
      <Paper sx={{ p: { xs: 1.5, sm: 2.5 }, borderRadius: 3, border: '1px solid #dbe7f3', bgcolor: '#f8fafc', mb: 2 }}>
        <Typography variant="h6" fontWeight={900}>
          {hasFullAccess ? 'All Driver Activities' : 'My Driver Activities'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {hasFullAccess
            ? 'Admin access shows every driver fuel record, trip log, pre-trip check-in, and post-trip check-in.'
            : 'This shows your fuel records, trip logs, pre-trip check-ins, and post-trip check-ins.'}
        </Typography>
        <Box sx={{ mt: 2, display: 'grid', gap: 1.25, gridTemplateColumns: { xs: '1fr', sm: '190px minmax(220px, 1fr)' } }}>
          <FormControl size="small">
            <InputLabel>Activity</InputLabel>
            <Select value={type} label="Activity" onChange={(event) => setType(event.target.value)}>
              <MenuItem value="all">All Activities</MenuItem>
              <MenuItem value="pre_trip">Pre-Trip Check-In</MenuItem>
              <MenuItem value="post_trip">Post-Trip Check-In</MenuItem>
              <MenuItem value="defect_report">Defect Reports</MenuItem>
              <MenuItem value="fuel">Fuel Records</MenuItem>
              <MenuItem value="trip">Trip Logs</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Search"
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="Search driver, vehicle, activity, or notes"
          />
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ py: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
      ) : filteredActivities.length === 0 ? (
        <Alert severity="info">No driver activities found.</Alert>
      ) : (
        <Stack spacing={1.5}>
          {filteredActivities.map((activity) => (
            <Card key={activity.id} variant="outlined" sx={{ borderRadius: 3, boxShadow: 'none' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <Box sx={{ width: 38, height: 38, borderRadius: '50%', bgcolor: '#eef6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                    {activityIcon(activity.type)}
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography fontWeight={900}>{activity.title}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
                          {activity.driver} • {activity.vehicle}
                        </Typography>
                        {activity.otherDriver ? (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Other Driver: {activity.otherDriver}
                          </Typography>
                        ) : null}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {statusChip(activity)}
                        <Chip size="small" label={formatDateTime(activity.occurredAt, activity.dateOnly)} variant="outlined" />
                      </Box>
                    </Box>
                    <Box sx={{ mt: 1, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {activity.details ? <Chip size="small" label={activity.details} /> : null}
                      {activity.odometer != null ? <Chip size="small" label={`Odometer ${Number(activity.odometer).toLocaleString()} km`} /> : null}
                      {activity.notes ? <Chip size="small" label={activity.notes} variant="outlined" sx={{ maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} /> : null}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}
